/**
 * Regras de threshold pra geladeiras/freezers.
 *
 * Lê metadata.setpoint_min e metadata.setpoint_max do equipment e
 * compara com a temperatura medida (interna ou produto).
 *
 * Status: ok | warn | alert
 *   ok:    dentro do range
 *   warn:  perto do limite (5% de margem por fora)
 *   alert: fora do range
 */

import { toNumber } from "./format";

export type ReadingStatus = "ok" | "warn" | "alert" | "stale" | "unknown";

export interface SetpointConfig {
  setpoint_min?: number | null;
  setpoint_max?: number | null;
  /** Tolerância percentual em torno do range (default 5%). */
  warn_tolerance?: number;
}

export interface ThresholdResult {
  status: ReadingStatus;
  /** Mensagem curta pra UI. */
  label: string;
}

const STALE_THRESHOLD_MS = 15 * 60 * 1000; // 15 min sem reading = stale

export function evaluateTemperature(
  value: unknown,
  cfg: SetpointConfig = {},
  lastSeen?: string | null,
): ThresholdResult {
  // Stale check
  if (lastSeen) {
    const age = Date.now() - new Date(lastSeen).getTime();
    if (age > STALE_THRESHOLD_MS) {
      return { status: "stale", label: "Sem dados recentes" };
    }
  }

  const v = toNumber(value);
  if (v === null) return { status: "unknown", label: "—" };

  const min = cfg.setpoint_min ?? null;
  const max = cfg.setpoint_max ?? null;
  if (min === null && max === null) {
    return { status: "ok", label: "Sem range definido" };
  }

  const tol = cfg.warn_tolerance ?? 0.5; // ±0.5 °C por padrão

  if (max !== null && v > max + tol) return { status: "alert", label: "Acima do limite" };
  if (min !== null && v < min - tol) return { status: "alert", label: "Abaixo do limite" };
  if (max !== null && v > max)       return { status: "warn", label: "Próximo do limite superior" };
  if (min !== null && v < min)       return { status: "warn", label: "Próximo do limite inferior" };

  return { status: "ok", label: "Dentro do range" };
}

/** Tom de cor pra Badge. */
export function statusTone(s: ReadingStatus): "success" | "warning" | "danger" | "neutral" {
  switch (s) {
    case "ok":     return "success";
    case "warn":   return "warning";
    case "alert":  return "danger";
    case "stale":  return "neutral";
    default:       return "neutral";
  }
}

/** Classes Tailwind pra texto colorido. */
export function statusTextColor(s: ReadingStatus): string {
  switch (s) {
    case "ok":     return "text-emerald-700";
    case "warn":   return "text-amber-700";
    case "alert":  return "text-red-700";
    case "stale":  return "text-slate-500";
    default:       return "text-slate-700";
  }
}

export function statusLabel(s: ReadingStatus): string {
  return {
    ok:      "OK",
    warn:    "Atenção",
    alert:   "Crítico",
    stale:   "Sem dados",
    unknown: "Sem leitura",
  }[s];
}
